import React, { useState, useEffect, useRef } from "react";
import { useModal, Modal } from "storybook-dashboard/components/modal";
import useApi, { fetcher } from "storybook-dashboard/utils/fetching";
import { BasicSpinner } from "storybook-dashboard/spinners/basicSpinner";
import Im from "immutable";
import { alertSuccess } from "traec-react/utils/sweetalert";
import { v4 as uuidv4 } from "uuid";
import { rpName } from "storybook-dashboard/dashboard/project/reportStatus";
import { TableReport } from "./index";
import ProjectContextWrapper from "storybook-dashboard/context/project";
import ReportContextWrapper from "storybook-dashboard/context/report";
import Moment from "moment";
import Octicon from "react-octicon";

const isPercent = (indicator) => {
  let name = (indicator?.get("name") || "").toLowerCase();
  return name.includes("%") || name.includes("percent");
};

const isInvalid = (indicator) => {
  return isPercent(indicator) && indicator.get("result_value") > 100;
};

function IndicatorMetricsList({ indicator }) {
  let metrics = indicator?.get("metrics") || Im.List();
  metrics = metrics.filter((metric) => metric?.get("value") !== null && metric?.get("value") !== undefined);
  metrics = metrics.sortBy((metric) => metric?.get("name"));
  return metrics.map((metric, i) => (
    <li key={i}>
      {metric?.get("name")} = {metric?.get("value")?.toFixed(2)}
    </li>
  ));
}

function ListInvalidIndicators({ indicators }) {
  return indicators?.map((indicator, index) => (
    <li key={index}>
      <strong>
        {index + 1}. {indicator.get("name")} = {indicator.get("result_value")?.toFixed(2)}
      </strong>
      <ul>
        <IndicatorMetricsList indicator={indicator} />
      </ul>
    </li>
  ));
}

const updateSuccess = ({ payload, redirectUrl, originalReport }) => {
  alertSuccess({
    text: `Thank you for submitting your data.  Your report was successfully updated.`,
    onConfirm: () => {
      location.href = redirectUrl;
    },
  });
};

const submitSuccess = ({ payload, redirectUrl, originalReport }) => {
  let reportingPeriodString = rpName(originalReport?.get("reporting_period_data"));
  alertSuccess({
    text: `Thank you for submitting your data for ${reportingPeriodString}.  Your report was successfully submitted.`,
    onConfirm: () => {
      location.href = redirectUrl;
    },
  });
};

const submitError = (err) => {
  alertSuccess({
    text: `There was an error submitting your report`,
    iconType: "error",
  });
};

const submitReport = (e, report, hideModal, setPending, method = "POST") => {
  e.preventDefault();
  setPending(true);
  let { uid: reportId, ref: refId, tracker: trackerId, project: projectId } = report?.toJS() || {};
  const formData = new FormData(e.target);
  const data = Object.fromEntries(formData.entries());

  console.log("submitReport submitting report...", report?.toJS(), data);

  let url = `/api/tracker/${trackerId}/ref/${refId}/commit/`;
  if (method !== "POST") {
    data.status = "UPDATE";
    url = `${url}${reportId}/`;
  }

  let redirectUrl = `/project/${projectId.substring(0, 8)}/wpack/${refId.substring(0, 8)}/evals`;

  console.log("submitReport Submitting report data...", url, method, data);

  fetcher(url, method, data)
    .then(({ payload }) => {
      let handler = method === "POST" ? submitSuccess : updateSuccess;
      hideModal();
      handler({ payload, redirectUrl, originalReport: report });
    })
    .catch((err) => {
      console.error("submitReport error", err);
      hideModal();
      submitError(err);
    });
};

function SubmitForm({ method, report, reportId }) {
  let [pending, setPending] = useState(false);
  let [comment, setComment] = useState("");
  let { hideModal } = useModal();
  console.log("SubmitModal", method, report?.toJS());

  let placeholder =
    (report || Im.Map()).getIn(["meta_json", "submit_placeholder"]) ||
    "This comment will be included in your submission record...";
  return (
    <form onSubmit={(e) => submitReport(e, report, hideModal, setPending, method)}>
      <div className="form-group">
        <p className="mb-1">
          <b>Final comment (required)</b>
        </p>
        <input
          name="comment"
          className="form-control"
          placeholder={placeholder}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </div>
      <button
        className="btn btn-sm btn-primary float-right"
        type="submit"
        disabled={pending || comment.trim().length < 1}
      >
        Submit
      </button>
    </form>
  );
}

const SubmitModalMessage = ({ warningCount, report, period }) => {
  const flaggedDataMessage = (
    <div className="alert alert-warning">
      <p className="mb-0">
        <b>
          <Octicon name="alert" className="mr-1" />
          Some data has been flagged for review.
        </b>{" "}
        To make changes, close this window. When you're ready, scroll down and submit to finalise your report.
      </p>
    </div>
  );
  return (
    <div>
      {warningCount > 0 ? flaggedDataMessage : null}
      <p>
        Reporting package: <b>{report.getIn(["tree_root", "name"])}</b>
      </p>
      <p>
        Reporting period: <b>{rpName(period)}</b>
      </p>
      <p>
        Submission deadline: <b>{Moment(report.get("due_date")).format("D MMM YYYY")}</b>
      </p>
    </div>
  );
};

function SubmitModalContent({ report, currentReportingPeriod, ...props }) {
  const [warningCount, setWarningCount] = useState(0);
  const modalRef = useRef(null);

  useEffect(() => {
    const countWarningBadgesInDOM = () => {
      if (modalRef.current) {
        const badges = modalRef.current.querySelectorAll(".badge.badge-warning");
        const checkBadges = Array.from(badges).filter((badge) => badge.textContent.trim() === "Check");
        setWarningCount(checkBadges.length);
        console.log(`Found ${checkBadges.length} warning badges in SubmitModal`);
      }
    };

    const timer = setTimeout(countWarningBadgesInDOM, 200); // delay to ensure badges are rendered

    return () => clearTimeout(timer);
  }, []);

  return (
    <div ref={modalRef}>
      <SubmitModalMessage warningCount={warningCount} report={report} period={currentReportingPeriod} />
      <TableReport {...props} disableInputs={true} disableComments={true} summaryReport={true} />
    </div>
  );
}

export function SubmitModal(props) {
  let { report, currentReportingPeriod } = props;
  let { uid: commitId, ref: refId, tracker: trackerId, project: projectId } = report?.toJS() || {};
  let params = { projectId, trackerId, commitId, refId };

  return (
    <Modal large={true} title="Final review (read only): confirm your report submission">
      <ProjectContextWrapper params={params}>
        <ReportContextWrapper params={params}>
          <SubmitModalContent {...props} report={report} currentReportingPeriod={currentReportingPeriod} />
        </ReportContextWrapper>
      </ProjectContextWrapper>
      <div className="my-5">
        <SubmitForm {...props} />
      </div>
    </Modal>
  );
}

function ClearModal() {
  let { hideModal } = useModal();
  useEffect(() => hideModal(), []);
  return null;
}

function ValidateIndicators(props) {
  let [invalidIndicators, setInvalidIndicators] = useState(Im.List());
  let { setModal, hideModal } = useModal();
  let {
    context: { commit },
  } = props;
  let reportId = commit.get("uid");

  // This is a hack to force a single call to the url per instance
  let [uuid, setUuid] = useState(`${uuidv4()}`);
  let {
    data: indicators,
    isLoading,
    isFetching,
  } = useApi(`/api/dashboard/report/${reportId}/indicator?requestId=${uuid}`);

  useEffect(() => {
    if (!indicators && !isLoading && !isFetching) {
      setModal(<SubmitModal {...props} report={commit} />, true);
    }
    if (indicators) {
      console.log("ValidateIndicators got indicators", indicators?.toJS());
      let invalids = indicators?.filter((i) => isInvalid(i));
      if (!invalids?.size) {
        setModal(<SubmitModal {...props} report={commit} />, true);
      } else {
        setInvalidIndicators(invalids);
      }
    }
  }, [indicators]);

  if (isLoading || isFetching) {
    return (
      <div className="my-2">
        <BasicSpinner />
      </div>
    );
  }

  // When there are no indicators, users can still submit the report
  if (!indicators && !isLoading && !isFetching) {
    setModal(<SubmitModal {...props} report={commit} />, true);
  }

  return (
    <div>
      <p>There's something unusual in your report. Please check the following indicators:</p>
      <ul>
        <ListInvalidIndicators indicators={invalidIndicators} />
      </ul>
      <div className="text-center">
        <div className="btn-group" role="group">
          <button
            className="btn btn-sm btn-danger mr-3"
            onClick={() => setModal(<SubmitModal {...props} report={commit} />, true)}
          >
            Ignore and proceed
          </button>
          <button className="btn btn-sm btn-success ml-3" onClick={() => setModal(<ClearModal />, true)}>
            Go back and edit
          </button>
        </div>
      </div>
    </div>
  );
}

export function ValidateAndSubmitModal(props) {
  return (
    <Modal title="Validating report..." hideClose={true}>
      <ValidateIndicators {...props} />
    </Modal>
  );
}
